import { useState } from 'react';
import './Blog.scss';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useNavigate } from 'react-router-dom';
import Popup from '../../components/Popup';
import Image from './7.jpg';
import Image1 from './7-1.jpg';

const BlogPage = () => {
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <div className='page'>
      <Header />
      <div className='blogPageMainScreen'>
        <div className='blogPageMainScreenContent'>
          <span className='blogPageMainScreenBack' onClick={() => navigate('/blog')}>
            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M7.9987 12.6667L3.33203 7.99999M3.33203 7.99999L7.9987 3.33333M3.33203 7.99999H12.6654'
                stroke='#3A6376'
                stroke-width='1.33'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
            </svg>
            Back to Articles
          </span>
          <h1 className='blogPageMainScreenTitle'>Burnout, Disengagement, and Low Morale</h1>
          <span className='blogPageMainScreenDate'>December 4, 2025</span>
          <div className='blogPageMainScreenImage'>
            <img src={Image} />
          </div>
        </div>
      </div>
      <div className='blogPageContent'>
        <h3>CRISIS OVERVIEW</h3>
        <p>
          First described by the clinical psychologist Herbert Freudenberger in 1974, the phrase “burnout” has evolved to become
          the professional catchall for symptoms of exhaustion, malaise, frustration, and inefficiency resulting from long-term
          job stress. Alarmingly, burnout rates among health care leaders and physicians are nearly double that of workers in
          other professions after taking into account hours worked, age, sex, and other factors.<sup>1</sup>
        </p>
        <p>
          In addition to burnout, disengagement and low morale can have an equally negative impact on a healthcare organization
          creating a ripple effect of reduced empathy and productivity that can spiral into serious quality and financial issues
          for healthcare providers if ignored. Gallup revealed that actively disengaged employees, about three in ten American
          workers, cost the U.S. $450 billion to $550 billion in productivity annually. What’s more, seven in ten professionals
          polled admit to not being committed to delivering their best performance.
        </p>
        <div className='blogPageContentQuote'>
          The Wall Street Journal reported on a study by the Harvard Medical School faculty, stating that, “96% of senior leaders
          reported feeling burnt out to some degree, with one-third describing their burnout as extreme.”
        </div>
        <h2>MITIGATION STRATEGIES</h2>
        <h4>TREAT THE CRISIS AS A C-SUITE PRIORITY</h4>
        <p>
          Healthcare leaders understand that promoting holistic health and well-being initiatives inevitably results in more
          satisfied employees and lower turnover, however, too often there is a lack of planning to properly execute the programs.
          Like any major organizational change there must be leadership support, the willingness to make a financial investment in
          improvements, and a skilled team capable of managing processes short, mid, and long term. The project champions
          addressing burnout, wellness, and morale should be trained in mental health strategies and have direct decision-making
          authority.
        </p>
        <h4>LEAD BY INDIVIDUAL EXAMPLE</h4>
        <p>
          Based on statistics, it’s as likely that the top leaders in a healthcare group are also some of the most affected by
          disengagement and burnout. To spark change, the company’s top leaders should begin by taking a hard look at themselves
          and their own state of wellness.
        </p>
        <p>
          Self-assessment of one’s own leadership style may help reveal areas for potential improvement. Top leaders take
          responsibility, acknowledge failure, and avoid blame to instill credibility. They roll up their sleeves as active
          participants and are courageous and persistent to create solutions that the team can rally behind. Most importantly,
          they are truthful, respectful, and fantastic listeners. Consider how your leadership team’s actions (or inactions) may
          be affecting team morale and invest in solutions to treat your own symptoms of burnout.
        </p>
        <p>
          A leadership team that can demonstrate professional effectiveness while living balanced mental and physical lives
          instills confidence in subordinates and peers that a healthy scenario is possible within a healthcare leadership career.
        </p>
        <h4>BECOME AN INVOLVED LEADER</h4>
        <p>
          Healthcare leaders can take an active role in their employee’s lives to ensure they’re watching for early signs of
          burnout. Knowing your employee’s concerns and addressing them early and often fosters trust and demonstrates mutual
          respect. Look for the following red-flag for opportunities to begin a proactive conversation if there is a potential
          concern:
        </p>
        <ul>
          <li>A decline in the quality and reduced enthusiasm for the work</li>
          <li>Withdrawal from conversations or being unusually silent</li>
          <li>Canceling meetings, tardiness, or frequent absenteeism</li>
          <li>Repetitive complaints about the same problem even after it has been addressed </li>
          <li>Lack of participation in problem-solving to resolve their direct concerns</li>
          <li>Complaining, blaming, or “venting” to peers</li>
          <li>Moodiness, tired, and excessively stressed</li>
        </ul>
        <br />
        <img src={Image1} style={{ width: '100%', height: 'auto' }} />
        <h4>ACTIVITY SOLICIT COMMUNICATION</h4>
        <p>
          Organizations that encourage employee communication create opportunities for engagement to address issues before they
          become crises. Open office hours, team luncheons, vision workshops, and town hall-style meetings serve to encourage
          conversation and open a window into the overall morale and wellness of your team.{' '}
        </p>
        <h4>CREATE A WELLNESS-FOCUSED CULTURE</h4>
        <p>
          Stress is simply a part of working in the healthcare world and caring employers prioritize treatment over termination.
          Offering employees the opportunity to address individual behaviors through education, tools, motivation, and social
          support starts them on a pathway toward developing healthier behaviors to decrease stress and their risk of chronic
          disease to improve their overall quality of life.
        </p>
        <p>
          In addition to having happier and healthier employees, healthcare organizations also benefit financially from developing
          a culture of wellness. Health Affairs published that for every dollar that was spent on a wellness program the company
          saved $3.27 because of reduced healthcare costs.
        </p>
        <h4>MAKE THE TOP-LEVEL CONNECTION</h4>
        <p>
          Employees need to feel invested and that their work contributes to the organizations business goals. In short, people
          want to matter. Taking the time to help your team understand how their efforts and job enrich the bigger picture builds
          their self-respect and helps reduce the chance of them not feeling heard or valued.
        </p>
        <h4>CHAMPION PROFESSIONAL DEVELOPMENT</h4>
        <p>
          Jack Welch famously said, “Before you are a leader, success is all about growing yourself. When you become a leader,
          success is all about growing others.” Offering continued learning and professional development programs let employees
          know that their individual growth is a priority to the organization. When a teammate is continuing to engage and learn
          in areas of professional interest, they feel more invested and in control of their career and life. Personal development
          is an often overlooked area of health and wellness cultures and is an area where leaders can make a major impact on
          employee satisfaction.
        </p>
        <i>
          Sources 1. Burnout and satisfaction with work-life balance among US physicians relative to the general US population.
          Shanafelt TD, Boone S, Tan L, et al. Arch Intern Med. 2012;172:1377–1385.
        </i>
      </div>

      {!!isPopupOpen && <Popup onClose={() => setIsPopupOpen(false)} />}

      <Footer />
    </div>
  );
};

export default BlogPage;
